import {
  addMobileMappingMissionReferences,
  createCurbAndGutterBreakline,
  createJerseyBarrierBreakline,
  createRoadCrownBreakline,
  createWorkspace,
  deleteWorkspace,
  deleteReference,
  listBreaklines,
  listReferences,
  listWorkspaces,
  type Client,
} from '@data-access/geospatial/fx-workspace-service';
import { UnreachableCaseError } from 'ts-essentials';
import { throwIfError } from '@trimble/http-utils';

import type { FxBreaklineType } from './fx-workspace-breakline.types';
import {
  DEFAULT_PAGE_SIZE,
  HTTP_NOT_FOUND,
  fetchAllPages,
  getClientHeaders,
  mapBreaklineResponse,
  mapWorkspaceResponse,
  unwrapOrThrow,
} from './api-fx-workspace-repository.helpers';
import type {
  FxWorkspaceRepository,
  SyncFxWorkspaceReferencesInput,
} from './fx-workspace-repository';
import type { CreateFxWorkspaceInput, FxWorkspace } from './fx-workspace.types';
import type { FxBreakline } from './fx-workspace-breakline.types';

export interface ApiFxWorkspaceRepositoryConfig {
  client: Client;
  clientName: string;
  clientVersion: string;
  getTenantTrn(): string | undefined;
}

export class ApiFxWorkspaceRepository implements FxWorkspaceRepository {
  constructor(private readonly config: ApiFxWorkspaceRepositoryConfig) {}

  public async list(): Promise<readonly FxWorkspace[]> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      return [];
    }

    const workspaces = await fetchAllPages(async (pageIndex) => {
      const response = await listWorkspaces({
        client: this.config.client,
        path: { tenantTrn },
        headers: getClientHeaders(
          this.config.clientName,
          this.config.clientVersion
        ),
        query: {
          pageIndex,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      return unwrapOrThrow(response, 'Failed to list FX workspaces');
    });

    return workspaces
      .filter((workspace) => workspace.isDeleted !== true)
      .map(mapWorkspaceResponse);
  }

  public async create(input: CreateFxWorkspaceInput): Promise<FxWorkspace> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error('Tenant TRN is required to create FX workspace');
    }

    const folderId = input.folderId?.trim();
    const body =
      folderId === undefined || folderId.length === 0
        ? { name: input.name.trim() }
        : { name: input.name.trim(), folderId };
    const response = await createWorkspace({
      client: this.config.client,
      path: { tenantTrn },
      body,
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    const created = unwrapOrThrow(response, 'Failed to create FX workspace');
    return mapWorkspaceResponse(created);
  }

  public async delete(workspaceTrn: string): Promise<void> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error('Tenant TRN is required to delete FX workspace');
    }

    const response = await deleteWorkspace({
      client: this.config.client,
      path: { tenantTrn, workspaceTrn },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    unwrapOrThrow(response, 'Failed to delete FX workspace');
  }

  public async listReferences(
    workspaceTrn: string
  ): Promise<readonly string[]> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      return [];
    }

    const references = await fetchAllPages(async (pageIndex) => {
      const response = await listReferences({
        client: this.config.client,
        path: { tenantTrn, workspaceTrn },
        headers: getClientHeaders(
          this.config.clientName,
          this.config.clientVersion
        ),
        query: {
          pageIndex,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      return unwrapOrThrow(response, 'Failed to list FX workspace references');
    });

    return references.map((reference) => reference.refTrn);
  }

  public async addReference(
    workspaceTrn: string,
    refTrn: string
  ): Promise<void> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error('Tenant TRN is required to add FX workspace reference');
    }

    const response = await addMobileMappingMissionReferences({
      client: this.config.client,
      path: { tenantTrn, workspaceTrn },
      body: { refTrns: [refTrn] },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    unwrapOrThrow(response, 'Failed to add FX workspace reference');
  }

  public async syncReferences(
    workspaceTrn: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<void> {
    const { toAdd, toRemove } = input;

    // Apply removals first so a failed add cannot leave refs that should
    // already be gone.
    for (const refTrn of toRemove) {
      await this.removeReference(workspaceTrn, refTrn);
    }

    for (const refTrn of toAdd) {
      await this.addReference(workspaceTrn, refTrn);
    }
  }

  public async removeReference(
    workspaceTrn: string,
    refTrn: string
  ): Promise<void> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error(
        'Tenant TRN is required to remove FX workspace reference'
      );
    }

    const response = await deleteReference({
      client: this.config.client,
      path: { tenantTrn, workspaceTrn, refTrn },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    // Deleting a reference is idempotent: a 404 means it is already absent,
    // which is the desired end state, so treat it as success.
    if (response.response?.status === HTTP_NOT_FOUND) {
      return;
    }

    throwIfError(response, 'Failed to remove FX workspace reference');
  }

  public async listBreaklines(
    workspaceTrn: string
  ): Promise<readonly FxBreakline[]> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      return [];
    }

    const breaklines = await fetchAllPages(async (pageIndex) => {
      const response = await listBreaklines({
        client: this.config.client,
        path: { tenantTrn, workspaceTrn },
        headers: getClientHeaders(
          this.config.clientName,
          this.config.clientVersion
        ),
        query: {
          pageIndex,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      return unwrapOrThrow(response, 'Failed to list FX workspace breaklines');
    });

    return breaklines
      .filter((item) => item.isDeleted !== true)
      .map(mapBreaklineResponse);
  }

  public async createBreakline(
    workspaceTrn: string,
    type: FxBreaklineType,
    name: string
  ): Promise<FxBreakline> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error(
        'Tenant TRN is required to create FX workspace breakline'
      );
    }

    const headers = getClientHeaders(
      this.config.clientName,
      this.config.clientVersion
    );
    const path = { tenantTrn, workspaceTrn };
    const body = { name: name.trim() };

    const response = await (async () => {
      switch (type) {
        case 'curbAndGutter':
          return createCurbAndGutterBreakline({
            client: this.config.client,
            path,
            body,
            headers,
          });
        case 'roadCrown':
          return createRoadCrownBreakline({
            client: this.config.client,
            path,
            body,
            headers,
          });
        case 'jerseyBarrier':
          return createJerseyBarrierBreakline({
            client: this.config.client,
            path,
            body,
            headers,
          });
        default:
          throw new UnreachableCaseError(type);
      }
    })();

    const created = unwrapOrThrow(
      response,
      'Failed to create FX workspace breakline'
    );
    return mapBreaklineResponse(created);
  }
}
